import React from 'react'
import PropTypes from 'prop-types'

import Button from '@material-ui/core/Button'
import Dialog from '@material-ui/core/Dialog'
import DialogTitle from '@material-ui/core/DialogTitle'
import DialogContent from '@material-ui/core/DialogContent'
import DialogActions from '@material-ui/core/DialogActions'
import CircularProgress from '@material-ui/core/CircularProgress'
import Grid from '@material-ui/core/Grid'
import Snackbar from '@material-ui/core/Snackbar'
import Alert from '@material-ui/lab/Alert'

import AddCircleIcon from '@material-ui/icons/AddCircle'
import CancelIcon from '@material-ui/icons/Cancel'

import { useForm } from 'react-hook-form'

import NewProductDialogCard from './NewProductDialogCard'

import ValidatedTextField from 'components/HookFormValidation/ValidatedTextField'
import ValidatedDropDown from 'components/HookFormValidation/ValidatedDropDown'
import ValidatedCheckbox from 'components/HookFormValidation/ValidatedCheckbox'

const ProductTypes = {
  Barcoded: 'barcoded',
  Unbarcoded: 'unbarcoded',
  PriceEmbedded: 'priceEmbedded'
}

export default function NewProductDialog (props) {
  const [adding, setAdding] = React.useState(false)
  const [validationFailed, setValidationFailed] = React.useState(false)

  const [barcodeDisabled, setBarcodeDisabled] = React.useState(false)

  // sort the drop down data into the right format for the component
  const productTypes = [{
    value: ProductTypes.Barcoded,
    displayString: 'Barcoded'
  },
  {
    value: ProductTypes.Unbarcoded,
    displayString: 'Non Barcoded'
  },
  {
    value: ProductTypes.PriceEmbedded,
    displayString: 'Price Embedded'
  }]
  const taxRates = props.taxRates.map((taxRate) => ({ value: taxRate.id, displayString: `${taxRate.taxRate}% - ${taxRate.description}` }))
  const categories = props.siteCategories.map((category) => ({ value: category.id, displayString: category.nodeListString }))
  const wholesalerFiles = props.wholesalerFiles.map((wholesalerFile) => ({ value: wholesalerFile.id, displayString: wholesalerFile.description }))
  const ageRestrictions = props.ageRestrictions.map((ageRestriction) => ({ value: ageRestriction.id, displayString: ageRestriction.age + '' })) // cast display value to string

  // reset the form upon open / close
  React.useEffect(() => {
    if (props.open) {
      reset() // reset the form to default values
    }

    setAdding(false)
  }, [props.open])

  const validateBarcode = (barcode) => {
    if (watchedProductType === ProductTypes.Barcoded) {
      if (!barcode.match(/^(?!02|20)\d{8}(?:\d{5})?$/)) {
        return ('Barcode must be 8 or 13 digits and not start with 02 or 20')
      }

      const barcodeWithoutCheckDigit = barcode.slice(0, -1)
      const checkDigit = calculateEANCheckDigit(barcodeWithoutCheckDigit)

      if (checkDigit === null || barcode !== `${barcodeWithoutCheckDigit}${checkDigit}`) {
        return ('The check digit for this barcode is invalid')
      }
    } else if (watchedProductType === ProductTypes.PriceEmbedded) {
      if (!barcode.match(/^(20|02)\d{5}$/)) {
        return ('Barcode should be of format 20xxxxx or 02xxxxx - please ignore remaining digits on printed barcode')
      }
    }

    if (props.onCheckBarcodeExists(barcode)) {
      return ('This barcode already exists')
    }

    return (true)
  }

  // This function takes a 7 or 12 digit barcode and calculates the
  // final EAN check digit and returns it.
  // Returns null if invalid number is passed.
  const calculateEANCheckDigit = (barcode) => {
    const digitArray = barcode.split('')

    if ((digitArray.length === 7) || (digitArray.length === 12) || (digitArray.length === 13)) {
      let digitSum = 0

      if (digitArray.length % 2 === 0) { // All barcodes EAN's need to have a odd number of digits for my algorithm
        digitArray.unshift(0)
      }

      // For check digit calculation, we need to multiply all even digits by 3,
      // and all odd digits by 1. We then need the sum of the results.
      for (let i = 0; i < digitArray.length; i++) {
        digitSum += parseInt((i % 2) ? digitArray[i] : (digitArray[i] * 3))
      }

      // We now need to get the nearest higest or equal 10 to the digitSum (round up if unit is 1 or above).
      const subtractor = (digitSum % 10) ? (Math.floor(digitSum / 10) + 1) * 10 : digitSum

      // Finally subtract the digitSum from the rounded number.
      const checkDigit = (subtractor - digitSum)

      return (checkDigit)
    }

    return (null)
  }

  const handleClose = (event, reason) => {
    // don't close by clicking background
    if (reason === 'backdropClick') {
      return
    }

    props.onClose(event, reason)
  }

  const handleErrorClose = (event, reason) => {
    console.log('reason:', reason)

    if (reason === 'timeout' || reason === 'clickaway') {
      setValidationFailed(false)
    }
  }

  const { handleSubmit, control, reset, watch, resetField, formState: { errors, touchedFields, isSubmitting, isValid } } = useForm({
    defaultValues: {
      barcode: '',
      productType: ProductTypes.Barcoded,
      description: '',
      shortDescription: '',
      itemSizeDesc: '',
      taxRate: '',
      category: '',
      ageCheck: 1,
      productCode: '',
      wholesalerFile: 8,
      packCost: 0,
      packSize: 1,
      unitRrp: 0,
      priceMarked: false,
      price: '',
      variablePriceProduct: false
    },
    mode: 'onTouched'
  })

  const watchedProductType = watch('productType') // watch the product type select element so we can manage
  const watchedVariablePriceProduct = watch('variablePriceProduct')

  React.useEffect(() => {
    if (isSubmitting && !isValid) {
      setValidationFailed(true)
    }
  }, [isSubmitting])

  const submitForm = (data) => {
    console.log('form data:', data)
    console.log('errors:', errors)
    console.log('touched fields:', touchedFields)

    setAdding(true)

    let type = 'priced'

    if (data.productType === ProductTypes.PriceEmbedded) {
      type = 'price_embedded'
    } else if (data.variablePriceProduct) {
      type = 'variable'
    }

    const result = props.onProductAdd({
      barcode: data.barcode === '' || data.barcode === undefined ? null : data.barcode,
      description: data.description,
      shortDescription: data.shortDescription,
      taxId: data.taxRate,
      itemSizeDescription: data.itemSizeDesc === '' ? null : data.itemSizeDesc,
      categoryId: data.category,
      productCode: data.productCode === '' ? null : data.productCode,
      wholesalerFileId: data.wholesalerFile,
      packCost: data.packCost,
      packSize: data.packSize,
      unitRrp: data.unitRrp,
      priceMarked: data.priceMarked,
      markupValue: watchedVariablePriceProduct || watchedProductType === ProductTypes.PriceEmbedded ? null : data.price,
      markupCalculationType: watchedVariablePriceProduct || watchedProductType === ProductTypes.PriceEmbedded ? null : 'Number',
      type: type,
      restrictedAgeId: data.ageCheck
    })

    console.log('result:', result)
  }

  React.useEffect(() => {
    if (watchedProductType === ProductTypes.Unbarcoded) {
      setBarcodeDisabled(true)
      resetField('barcode')
    } else {
      setBarcodeDisabled(false)
    }
  }, [watchedProductType])

  React.useEffect(() => {
    if (watchedVariablePriceProduct) {
      resetField('price')
    }
  }, [watchedVariablePriceProduct])

  return (
    <>
      <Snackbar open={validationFailed} autoHideDuration={5000} onClose={handleErrorClose}>
        <Alert variant="filled" severity="error">
            Please fill in all required fields and fix validation issues to continue
        </Alert>
      </Snackbar>

      <Dialog maxWidth="md" fullWidth={true} open={props.open} onClose={handleClose}>
        <DialogTitle>New Product</DialogTitle>
        <DialogContent dividers>
          <form id="newProductForm" noValidate onSubmit={handleSubmit((data) => submitForm(data))}>
            <NewProductDialogCard title="Product Identifier">
              <Grid container spacing={2}>
                <Grid item xs={3}>
                  <ValidatedDropDown
                    name="productType"
                    label="Product Type"
                    control={control}
                    errors={errors}
                    options={productTypes}
                    rules={{
                      required: true
                    }}
                  />
                </Grid>
                <Grid item xs={5}>
                  <ValidatedTextField
                    name="barcode"
                    label="Barcode"
                    control={control}
                    errors={errors}
                    placeholder="e.g. 5000159319829"
                    rules={{
                      required: 'Barcode is required',
                      validate: validateBarcode,
                      disabled: barcodeDisabled
                    }}
                  />
                </Grid>
              </Grid>
            </NewProductDialogCard>

            <NewProductDialogCard title="Product Information">
              <Grid container spacing={2}>
                <Grid item xs={7}>
                  <ValidatedTextField
                    control={control}
                    errors={errors}
                    name="description"
                    label="Description"
                    placeholder="e.g. Mars Bar"
                    rules={{
                      minLength: {
                        value: 2,
                        message: 'Description must be between 2 and 100 characters long'
                      },
                      maxLength: {
                        value: 100,
                        message: 'Description must be between 2 and 100 characters long'
                      },
                      required: 'Description is required'
                    }}
                  />
                </Grid>
                <Grid item xs={5}>
                  <ValidatedTextField
                    control={control}
                    errors={errors}
                    name="shortDescription"
                    label="Short Description"
                    helperText="If left blank first 50 characters of Description will be used"
                    rules={{
                      maxLength: {
                        value: 50,
                        message: 'Short Description must be less than 50 characters long'
                      }
                    }}
                  />
                </Grid>

                <Grid item xs={7}>
                  <ValidatedDropDown
                    control={control}
                    errors={errors}
                    name="taxRate"
                    label="Tax Rate"
                    options={taxRates}
                    rules={{
                      required: 'Tax Rate is required'
                    }}
                  />
                </Grid>

                <Grid item xs={5}>
                  <ValidatedTextField
                    control={control}
                    errors={errors}
                    name="itemSizeDesc"
                    label="Item Size Description"
                    placeholder="e.g. 35g"
                    rules={{
                      maxLength: {
                        value: 20,
                        message: 'Item Size Description must be no longer than 20 characters long'
                      }
                    }}
                  />
                </Grid>

                <Grid item xs={9}>
                  <ValidatedDropDown
                    control={control}
                    errors={errors}
                    name="category"
                    label="Category"
                    options={categories}
                    rules={{
                      required: 'You must select a Category'
                    }}
                  />
                </Grid>

                <Grid item xs={3}>
                  <ValidatedDropDown
                    control={control}
                    errors={errors}
                    name="ageCheck"
                    label="Age Restriction"
                    options={ageRestrictions}
                  />
                </Grid>
              </Grid>
            </NewProductDialogCard>

            <NewProductDialogCard title="Wholesaler Information">
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <ValidatedTextField
                    control={control}
                    errors={errors}
                    name="productCode"
                    label="Product Code"
                    placeholder="e.g. 083477"
                    rules={{
                      pattern: {
                        value: /^[a-zA-Z0-9]{0,20}$/,
                        message: 'Product Code must be no more than 20 alphanumeric characters long'
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <ValidatedDropDown
                    control={control}
                    errors={errors}
                    name="wholesalerFile"
                    label="Wholesaler File"
                    options={wholesalerFiles}
                    rules={{
                      required: 'You must select a Wholesaler File'
                    }}
                  />
                </Grid>
              </Grid>
            </NewProductDialogCard>

            {
              watchedProductType !== ProductTypes.PriceEmbedded &&
              <NewProductDialogCard title="Cost Information">
                <Grid container spacing={2}>
                  <Grid item xs={3}>
                    <ValidatedTextField
                      control={control}
                      errors={errors}
                      name="packCost"
                      label="Pack Cost"
                      type="number"
                      helperText="Value in pence"
                      rules={{
                        valueAsNumber: true,
                        required: 'Pack Cost is required',
                        min: {
                          value: 0,
                          message: 'Pack Cost must be >= 0'
                        },
                        max: {
                          value: 1000000,
                          message: 'Pack Cost must be < 1,000,000'
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={3}>
                    <ValidatedTextField
                      control={control}
                      errors={errors}
                      name="packSize"
                      label="Pack Size"
                      type="number"
                      rules={{
                        valueAsNumber: true,
                        required: 'Pack Size is required',
                        min: {
                          value: 1,
                          message: 'Pack Size must be > 1'
                        },
                        max: {
                          value: 1000000,
                          message: 'Pack Size must be < 1,000,000'
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={3}>
                    <ValidatedTextField
                      control={control}
                      errors={errors}
                      name="unitRrp"
                      label="Unit RRP"
                      type="number"
                      helperText="Value in pence"
                      rules={{
                        valueAsNumber: true,
                        required: 'Unit RRP is required',
                        min: {
                          value: 0,
                          message: 'Unit RRP must be > 0 or more'
                        },
                        max: {
                          value: 1000000,
                          message: 'Unit RRP must be < 1,000,000'
                        }
                      }}
                    />
                  </Grid>

                  <Grid item xs={3}>
                    <ValidatedCheckbox
                      name="priceMarked"
                      label="Price Marked"
                      control={control}
                      errors={errors}
                    />
                  </Grid>
                </Grid>
              </NewProductDialogCard>
            }

            {
              watchedProductType !== ProductTypes.PriceEmbedded &&
              <NewProductDialogCard title="Pricing">
                <Grid container spacing={2}>
                  <Grid item xs={3}>
                    <ValidatedTextField
                      control={control}
                      errors={errors}
                      name="price"
                      label="Selling Price"
                      type="number"
                      helperText="Value in pence"
                      rules={{
                        valueAsNumber: true,
                        disabled: watchedVariablePriceProduct,
                        required: 'Selling Price is required',
                        min: {
                          value: 1,
                          message: 'Selling Price must be > 0'
                        },
                        max: {
                          value: 1000000,
                          message: 'Selling Price must be < 1,000,000'
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={9}>
                    <ValidatedCheckbox
                      name="variablePriceProduct"
                      label="Variable Price Product"
                      helperText="Price is set at time of sale"
                      type="switch"
                      control={control}
                      errors={errors}
                    />
                  </Grid>
                </Grid>
              </NewProductDialogCard>
            }
          </form>
        </DialogContent>
        <DialogActions>
          <Button autoFocus variant="contained" type="submit" form="newProductForm" color="primary">
            {
              adding === true
                ? <><CircularProgress color="inherit" size={20} />&nbsp;&nbsp;</>
                : <><AddCircleIcon />&nbsp;</>
            }
              Add
          </Button>
          <Button variant="contained" onClick={props.onClose} color="secondary">
            <CancelIcon />&nbsp;Cancel
          </Button>
        </DialogActions>
      </Dialog>

    </>
  )
}

NewProductDialog.propTypes = {
  onClose: PropTypes.func.isRequired,
  onProductAdd: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  siteCategories: PropTypes.array.isRequired,
  ageRestrictions: PropTypes.array.isRequired,
  taxRates: PropTypes.array.isRequired,
  wholesalerFiles: PropTypes.array.isRequired,
  onCheckBarcodeExists: PropTypes.func.isRequired,
  productDescriptions: PropTypes.array.isRequired
}
